import { useState, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { useAuth } from '../../../../../contexts/AuthContext';

interface Client {
  id: string;
  first_name: string;
  last_name: string;
  phone?: string;
}

interface VisitVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: VisitVoucherData) => void;
}

export interface VisitVoucherData {
  firstName: string;
  lastName: string;
  phone?: string;
  idType?: 'id-card' | 'passport';
  idNumber?: string;
  clientId?: string;
}

export const VisitVoucherModal = ({ isOpen, onClose, onConfirm }: VisitVoucherModalProps) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<VisitVoucherData>({
    firstName: '',
    lastName: '',
    phone: '',
    idType: 'id-card',
    idNumber: '',
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [suggestedClients, setSuggestedClients] = useState<Client[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (searchTerm.length >= 3) {
      searchClients(searchTerm);
    } else {
      setSuggestedClients([]);
      setShowSuggestions(false);
    }
  }, [searchTerm]);

  const searchClients = async (term: string) => {
    try {
      setIsLoading(true);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
      
      const response = await fetch(`${baseUrl}/clients?agency_id=${user?.agency_id || 1}&status=active`);
      
      if (!response.ok) throw new Error('Erreur lors de la recherche');
      
      const data = await response.json();
      
      // Filtrer côté client avec le terme de recherche
      const filtered = data.filter((client: Client) => {
        const fullName = `${client.first_name} ${client.last_name}`.toLowerCase();
        return fullName.includes(term.toLowerCase());
      });
      
      setSuggestedClients(filtered.slice(0, 10));
      setShowSuggestions(true);
    } catch (err) {
      console.error('Erreur lors de la recherche:', err);
      setSuggestedClients([]);
    } finally {
      setIsLoading(false);
    }
  };

  const selectClient = (client: Client) => {
    setFormData(prev => ({
      ...prev,
      firstName: client.first_name,
      lastName: client.last_name,
      phone: client.phone || '',
      clientId: client.id,
    }));
    setSearchTerm(`${client.first_name} ${client.last_name}`);
    setShowSuggestions(false);
  };

  const handleConfirm = async () => {
    if (!formData.firstName || !formData.lastName) {
      alert('Le nom et prénom sont nécessaires');
      return;
    }

    if (formData.clientId) {
      onConfirm(formData);
    } else {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
        
        const clientData = {
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone || '',
          civility: 'M.',
          address: 'Non spécifié',
          wilaya: 'Alger',
          city: 'Alger',
          transaction_type: 'sale',
          property_type: 'Appartement',
          min_budget: 0,
          max_budget: 0,
          min_area: 0,
          min_rooms: 0,
          interest_level: 'to-contact',
          agency_id: user?.agency_id || 1,
          created_by_user_id: user?.id || 1,
        };

        console.log('Création client avec données:', clientData);

        const response = await fetch(`${baseUrl}/clients`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(clientData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Erreur serveur:', errorData);
          throw new Error(errorData.error || 'Erreur lors de la création du client');
        }
        
        const newClient = await response.json();
        
        onConfirm({
          ...formData,
          clientId: newClient.id,
        });
      } catch (err) {
        console.error('Erreur lors de la création du client:', err);
        alert(`Erreur lors de la création du client: ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
      }
    }
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Bon de visite</h2>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Rechercher un client
            </label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  placeholder="Tapez 3 caractères..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>

              {showSuggestions && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                  {isLoading ? (
                    <div className="p-3 text-gray-500 dark:text-gray-400 text-sm">Recherche...</div>
                  ) : suggestedClients.length > 0 ? (
                    suggestedClients.map(client => (
                      <button
                        key={client.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          selectClient(client);
                        }}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 transition border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {client.first_name} {client.last_name}
                        </div>
                        {client.phone && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">{client.phone}</div>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="p-3 text-gray-500 dark:text-gray-400 text-sm">
                      Aucun client trouvé - créez-en un nouveau
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Prénom
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                placeholder="Prénom"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Nom
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                placeholder="Nom"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={e => setFormData({ ...formData, phone: e.target.value })}
              onClick={(e) => e.stopPropagation()}
              placeholder="Téléphone"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Type de pièce d'identité
              </label>
              <select
                value={formData.idType}
                onChange={e => setFormData({ ...formData, idType: e.target.value as 'id-card' | 'passport' })}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="id-card">Carte d'identité</option>
                <option value="passport">Passeport</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Numéro de pièce d'identité
              </label>
              <input
                type="text"
                value={formData.idNumber}
                onChange={e => setFormData({ ...formData, idNumber: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                placeholder="Numéro"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
        </div>

        <div 
          className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition font-medium"
          >
            Annuler
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleConfirm();
            }}
            className="flex-1 px-4 py-2 text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition font-medium"
          >
            Imprimer
          </button>
        </div>
      </div>
    </div>
  );
};
